/**
 * Google Analytics 4 (gtag.js) Service
 *
 * Gère l'initialisation et le tracking des événements Google Analytics.
 * Respecte le RGPD : GA n'est chargé qu'après consentement analytics.
 */

import { isGALoaded } from "../ga/loadGA";

// Déclaration du type global pour gtag
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

// État de l'initialisation
let isGAInitialized = false;
let isScriptLoaded = false;

/**
 * Obtient l'ID de Google Analytics depuis les variables d'environnement
 * Fallback sur l'ID de production si la variable n'est pas définie
 */
const getGAId = (): string | undefined => {
  // Priorité à la variable d'environnement, fallback sur l'ID de production
  return (
    import.meta.env.VITE_GA_MEASUREMENT_ID ||
    import.meta.env.VITE_PUBLIC_GA_MEASUREMENT_ID ||
    import.meta.env.VITE_APP_GA_MEASUREMENT_ID ||
    "G-SVLGQM6J5M"
  );
};

/**
 * Vérifie si Google Analytics est initialisé et prêt à tracker
 * Vérifie à la fois le nouveau système (loadGA) et l'ancien système pour compatibilité
 * Ajoute aussi une vérification directe de gtag/dataLayer pour plus de robustesse
 */
export const isGAReady = (): boolean => {
  // Vérifier d'abord le nouveau système (loadGA)
  const newSystemReady = isGALoaded();
  
  // Vérification directe : si gtag et dataLayer existent avec une config GA, considérer comme prêt
  // Cela permet de fonctionner même si les variables internes ne sont pas mises à jour
  // Also check if the GA script is loaded in the DOM
  const scriptLoaded = typeof document !== "undefined" && 
    !!document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
  
  const directCheck = (
    typeof window !== "undefined" &&
    typeof window.gtag === "function" &&
    Array.isArray(window.dataLayer) &&
    scriptLoaded && // Ensure script is actually in DOM
    // Check if there's a config OR if gtag function exists (real gtag, not placeholder)
    (window.dataLayer.some(item => 
      Array.isArray(item) && item[0] === 'config' && item[1] === getGAId()
    ) || scriptLoaded) // If script is loaded, gtag should work
  );
  
  // Vérifier aussi l'ancien système pour compatibilité
  const oldSystemReady = (
    isGAInitialized &&
    isScriptLoaded &&
    typeof window !== "undefined" &&
    typeof window.gtag === "function" &&
    typeof window.dataLayer !== "undefined"
  );
  
  const ready = newSystemReady || directCheck || oldSystemReady;
  
  // Log en dev pour debug (production-safe)
  if (!ready && import.meta.env.DEV) {
    console.debug("[Google Analytics] Not ready:", {
      newSystemReady,
      directCheck,
      oldSystemReady,
      isGAInitialized,
      isScriptLoaded,
      hasGtag: typeof window !== "undefined" && typeof window.gtag === "function",
      hasDataLayer: typeof window !== "undefined" && typeof window.dataLayer !== "undefined",
      hasConfig: window.dataLayer?.some(item => 
        Array.isArray(item) && item[0] === 'config' && item[1] === getGAId()
      ) || false,
    });
  }
  
  return ready;
};

/**
 * Initialise Google Analytics 4
 * Cette fonction charge le script gtag.js et initialise GA avec l'ID configuré.
 * Elle doit être appelée uniquement après le consentement analytics de l'utilisateur.
 */
export const initGoogleAnalytics = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const gaId = getGAId();

    if (!gaId) {
      if (import.meta.env.DEV) {
        console.warn("[Google Analytics] Aucun ID GA configuré");
      }
      resolve();
      return;
    }

    if (typeof window === "undefined") {
      resolve();
      return;
    }

    // Éviter la double initialisation - mais seulement si le script est déjà complètement chargé
    if (isGAInitialized && isScriptLoaded && window.gtag) {
      console.debug("[Google Analytics] Déjà initialisé, skip");
      resolve();
      return;
    }

    // Vérifier si le script est déjà chargé dans le head (nouvelle approche)
    const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`);
    
    if (existingScript && typeof window.gtag === "function" && window.dataLayer) {
      // Script déjà chargé dans le head, activer le tracking maintenant que le consentement est donné
      try {
        window.gtag("config", gaId, {
          send_page_view: false, // On gère les page views manuellement pour SPA
          anonymize_ip: true,
        });
        
        isGAInitialized = true;
        isScriptLoaded = true;
        
        console.debug("[Google Analytics] ✅ Tracking activé (script déjà chargé):", gaId);
        resolve();
        return;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("[Google Analytics] Erreur activation tracking:", error);
        }
        // Continue avec le fallback dynamique
      }
    }

    // Fallback : chargement dynamique si le script n'est pas dans le head
    try {
      // Initialiser dataLayer avant le chargement du script
      window.dataLayer = window.dataLayer || [];

      // Définir la fonction gtag locale avant le chargement du script
      // Cette fonction sera remplacée par la vraie fonction gtag de Google après le chargement
      function gtagLocal(...args: unknown[]) {
        window.dataLayer.push(args);
      }

      // Ne pas écraser gtag s'il existe déjà
      if (!window.gtag) {
        window.gtag = gtagLocal;
        gtagLocal("js", new Date());
      }

      // Charger le script Google Analytics 4 (gtag.js)
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;

      script.onload = () => {
        // Attendre que le script externe soit complètement chargé et ait remplacé notre fonction
        const checkScriptReady = () => {
          // Vérifier si le script externe a remplacé notre fonction gtag locale
          // Le script externe définit gtag sur window, donc on vérifie si c'est bien chargé
          const scriptTag = document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`);
          
          if (scriptTag && window.dataLayer && window.gtag) {
            // Configurer GA avec l'ID
            window.gtag("config", gaId, {
              send_page_view: false, // On gère les page views manuellement pour SPA
            });
            
            isGAInitialized = true;
            isScriptLoaded = true;

            // Log pour debug (production-safe avec console.debug)
            console.debug("[Google Analytics] ✅ Initialisé avec succès:", gaId);
            console.debug("[Google Analytics] dataLayer:", window.dataLayer);
            console.debug("[Google Analytics] gtag:", typeof window.gtag);

            resolve();
          } else {
            // Réessayer après un court délai
            setTimeout(checkScriptReady, 50);
          }
        };

        // Commencer la vérification après un court délai
        setTimeout(checkScriptReady, 100);
      };

      script.onerror = () => {
        const errorMsg = "[Google Analytics] Erreur de chargement du script";
        console.error(errorMsg);
        reject(new Error(errorMsg));
      };

      // Insérer le script dans le DOM
      const firstScript = document.getElementsByTagName("script")[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("[Google Analytics] Erreur d'initialisation:", error);
      }
      reject(error);
    }
  });
};

/**
 * Track un événement PageView
 * Appelé automatiquement lors des navigations SPA
 * @param path - Chemin de la page (optionnel, par défaut window.location.pathname)
 */
export const trackPageView = (path?: string): void => {
  if (!isGAReady()) {
    if (import.meta.env.DEV) {
      console.debug("[Google Analytics] Not ready, skipping PageView");
    }
    return;
  }

  // Vérifier que le script externe est bien chargé
  const scriptTag = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
  if (!scriptTag) {
    console.warn("[Google Analytics] Script GA non trouvé dans le DOM, skip PageView");
    return;
  }

  try {
    const pagePath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
    const gaId = getGAId();

    if (!gaId) {
      console.warn("[Google Analytics] Pas d'ID GA pour PageView");
      return;
    }

    // Vérifier que gtag est bien une fonction avant d'appeler
    if (typeof window.gtag !== "function") {
      console.warn("[Google Analytics] gtag n'est pas une fonction, skip PageView");
      return;
    }

    // Mettre à jour la configuration avec le nouveau chemin de page
    window.gtag("config", gaId, {
      page_path: pagePath,
      page_title: typeof document !== "undefined" ? document.title : "",
    });

    // Envoyer explicitement l'événement page_view pour GA4
    // Cela garantit que l'événement est bien envoyé à Google Analytics
    window.gtag("event", "page_view", {
      page_path: pagePath,
      page_title: typeof document !== "undefined" ? document.title : "",
      page_location: typeof window !== "undefined" ? window.location.href : "",
    });

    // Log pour debug (production-safe avec console.log pour visibilité)
    console.log("[Google Analytics] 📊 PageView tracké:", pagePath);
    console.log("[Google Analytics] dataLayer length après page_view:", window.dataLayer?.length || 0);
    
    // Vérifier que l'événement a bien été ajouté au dataLayer
    if (window.dataLayer && window.dataLayer.length > 0) {
      const lastEvent = window.dataLayer[window.dataLayer.length - 1];
      if (Array.isArray(lastEvent) && lastEvent[0] === "event" && lastEvent[1] === "page_view") {
        console.log("[Google Analytics] ✅ Événement page_view ajouté au dataLayer");
      }
    }
  } catch (error) {
    console.error("[Google Analytics] Erreur PageView:", error);
  }
};

/**
 * Track un événement personnalisé
 * @param eventName - Nom de l'événement (ex: 'purchase', 'sign_up', 'search')
 * @param eventParams - Paramètres de l'événement (optionnel)
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, unknown>
): void => {
  if (!isGAReady()) {
    return;
  }

  try {
    if (eventParams) {
      window.gtag("event", eventName, eventParams);
    } else {
      window.gtag("event", eventName);
    }

    // Log pour debug (production-safe avec console.debug)
    console.debug(`[Google Analytics] ✅ Event "${eventName}" tracké:`, eventParams || "(sans paramètres)");
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error(`[Google Analytics] Erreur event "${eventName}":`, error);
    }
  }
};

/**
 * WhatsApp CTA. Event name must stay `whatsapp_click` for the GA4 custom event.
 * Fired whenever gtag exists, including before the stricter GA-ready gate.
 */
export const trackWhatsAppClick = (label?: string): void => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "whatsapp_click", {
      event_category: "Contact",
      event_label: label ?? window.location.pathname,
    });
  }
};

/**
 * Track un événement de conversion (achat/réservation)
 * @param value - Montant de la transaction
 * @param currency - Devise (par défaut MAD)
 * @param transactionId - ID de la transaction (optionnel)
 * @param items - Liste des items (optionnel)
 */
export const ADS_PURCHASE_STORAGE_PREFIX = "rakb_gads_purchase_";

/**
 * Conversion Google Ads officielle (gtag conversion), une fois par booking_id.
 * No-op si VITE_GOOGLE_ADS_CONVERSION_SEND_TO est absent. N'altère pas l'event GA4 purchase.
 */
export const trackGoogleAdsPurchase = (
  bookingId: string,
  value: number,
  currency: string = "MAD"
): void => {
  const sendTo = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_SEND_TO as string | undefined;
  if (!sendTo || !bookingId || !Number.isFinite(value) || value <= 0) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;

  const storageKey = `${ADS_PURCHASE_STORAGE_PREFIX}${bookingId}`;
  try {
    if (window.localStorage.getItem(storageKey) === "1") return;
    window.gtag("event", "conversion", {
      send_to: sendTo,
      value,
      currency,
      transaction_id: bookingId,
    });
    window.localStorage.setItem(storageKey, "1");
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[Google Ads] Conversion non envoyée:", error);
    }
  }
};

export const trackPurchase = (
  value: number,
  currency: string = "MAD",
  transactionId?: string,
  items?: Array<{
    item_id?: string;
    item_name?: string;
    price?: number;
    quantity?: number;
  }>
): void => {
  if (transactionId) {
    trackGoogleAdsPurchase(transactionId, value, currency);
  }

  if (!isGAReady()) {
    return;
  }

  try {
    const eventParams: Record<string, unknown> = {
      value,
      currency,
    };

    if (transactionId) {
      eventParams.transaction_id = transactionId;
    }

    if (items && items.length > 0) {
      eventParams.items = items;
    }

    if (transactionId) {
      try {
        if (window.localStorage.getItem(`rakb_ga_purchase_${transactionId}`) === "1") return;
      } catch {
        // storage indisponible : on envoie quand même cette fois
      }
    }

    window.gtag("event", "purchase", eventParams);
    if (transactionId) {
      try {
        window.localStorage.setItem(`rakb_ga_purchase_${transactionId}`, "1");
      } catch {
        // le flag Ads suffit pour la conversion payante
      }
    }

    console.debug("[Google Analytics] ✅ Purchase tracké:", eventParams);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[Google Analytics] Erreur Purchase:", error);
    }
  }
};

/**
 * Track un événement de début de checkout
 * @param value - Montant estimé
 * @param currency - Devise (par défaut MAD)
 * @param meta - Métadonnées funnel (véhicule, protection)
 */
export const trackBeginCheckout = (
  value?: number,
  currency: string = "MAD",
  meta?: {
    vehicle_id?: string;
    total_amount?: number;
    protection_tier?: string;
  }
): void => {
  if (!isGAReady()) {
    return;
  }

  try {
    const eventParams: Record<string, unknown> = {
      currency,
    };

    if (value !== undefined) {
      eventParams.value = value;
    }
    if (meta?.total_amount !== undefined) {
      eventParams.total_amount = meta.total_amount;
    } else if (value !== undefined) {
      eventParams.total_amount = value;
    }
    if (meta?.vehicle_id) {
      eventParams.vehicle_id = meta.vehicle_id;
    }
    if (meta?.protection_tier) {
      eventParams.protection_tier = meta.protection_tier;
    }

    window.gtag("event", "begin_checkout", eventParams);

    console.debug("[Google Analytics] ✅ BeginCheckout tracké:", eventParams);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[Google Analytics] Erreur BeginCheckout:", error);
    }
  }
};

/**
 * Track search_performed (ad → search funnel)
 */
export const trackSearchPerformed = (params: {
  city: string;
  pickup_date: string;
  return_date: string;
  results_count: number;
}): void => {
  trackEvent("search_performed", {
    city: params.city || "unknown",
    pickup_date: params.pickup_date,
    return_date: params.return_date,
    results_count: params.results_count,
  });
};

/**
 * Track protection tier selection
 */
export const trackSelectProtectionTier = (params: {
  tier_name: string;
  tier_price: number;
}): void => {
  trackEvent("select_protection_tier", {
    tier_name: params.tier_name,
    tier_price: params.tier_price,
  });
};

/**
 * Agency signup step 1 completed
 */
export const trackAgencySignupStep1Completed = (params: {
  city: string;
  fleet_size: string;
}): void => {
  trackEvent("agency_signup_step1_completed", {
    city: params.city || "unknown",
    fleet_size: params.fleet_size || "unknown",
  });
};

/**
 * Agency first car drafted
 */
export const trackAgencyFirstCarDrafted = (params: {
  vehicle_id: string;
  category: string;
}): void => {
  trackEvent("agency_first_car_drafted", {
    vehicle_id: params.vehicle_id,
    category: params.category || "unknown",
  });
};

/**
 * Track un événement de vue de contenu (vue d'un véhicule)
 * @param itemId - ID du véhicule
 * @param itemName - Nom du véhicule
 * @param value - Prix journalier (optionnel)
 * @param currency - Devise (par défaut MAD)
 * @param meta - total_price et autres props funnel
 */
export const trackViewItem = (
  itemId: string,
  itemName: string,
  value?: number,
  currency: string = "MAD",
  meta?: {
    price_per_day?: number;
    total_price?: number;
  }
): void => {
  if (!isGAReady()) {
    return;
  }

  try {
    const pricePerDay = meta?.price_per_day ?? value;
    const eventParams: Record<string, unknown> = {
      vehicle_id: itemId,
      vehicle_name: itemName,
      currency,
      items: [
        {
          item_id: itemId,
          item_name: itemName,
          ...(pricePerDay !== undefined && { price: pricePerDay }),
        },
      ],
    };

    if (pricePerDay !== undefined) {
      eventParams.value = pricePerDay;
      eventParams.price_per_day = pricePerDay;
    }
    if (meta?.total_price !== undefined) {
      eventParams.total_price = meta.total_price;
    }

    window.gtag("event", "view_item", eventParams);

    console.debug("[Google Analytics] ✅ ViewItem tracké:", eventParams);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[Google Analytics] Erreur ViewItem:", error);
    }
  }
};

/**
 * Track un événement de génération de lead (inscription, création de compte)
 */
export const trackLead = (): void => {
  if (!isGAReady()) {
    return;
  }

  try {
    window.gtag("event", "generate_lead");

    // Log pour debug (production-safe avec console.debug)
    console.debug("[Google Analytics] ✅ GenerateLead tracké");
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[Google Analytics] Erreur GenerateLead:", error);
    }
  }
};

/**
 * Fonction de diagnostic pour vérifier l'état de Google Analytics
 * À appeler depuis la console du navigateur pour debug
 */
export const diagnoseGA = (): void => {
  const gaId = getGAId();
  const diagnostics = {
    "GA ID": gaId || "NON CONFIGURÉ",
    "isGAInitialized": isGAInitialized,
    "isScriptLoaded": isScriptLoaded,
    "window.gtag exists": typeof window !== "undefined" && typeof window.gtag === "function",
    "window.dataLayer exists": typeof window !== "undefined" && typeof window.dataLayer !== "undefined",
    "dataLayer length": typeof window !== "undefined" && window.dataLayer ? window.dataLayer.length : 0,
    "isGAReady()": isGAReady(),
    "Script in DOM": typeof document !== "undefined" && !!document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`),
  };

  console.log("=== DIAGNOSTIC GOOGLE ANALYTICS ===");
  console.table(diagnostics);
  console.log("dataLayer:", window.dataLayer);
  console.log("gtag function:", window.gtag);
  console.log("================================");

  return diagnostics;
};

// Exposer la fonction de diagnostic globalement pour faciliter le debug
if (typeof window !== "undefined") {
  (window as any).diagnoseGA = diagnoseGA;
}

